import { msalInstance } from './msalInstance';
import { tokenRequest } from './msalConfig';

/**
 * The API's access token, fresh - as services/ecm-api.ts has always asked for it. MSAL hands back its
 * cached token while that is valid and renews it silently once it has expired, so a page left open
 * past the token's lifetime (about an hour) keeps working instead of answering 401 "Unauthorized -
 * valid authentication token required".
 *
 * The token saved at sign-in is the fallback: when MSAL cannot renew without the person signing in
 * again, or a sign-in is in progress, the saved token goes as before and the backend answers 401 if
 * it has expired. Each fresh token replaces the saved one, so code that reads sessionStorage itself
 * gets it too. Signed out (nothing saved) it answers null and asks MSAL for nothing. Callers at the
 * same moment - a page load fires several requests at once - share one request to MSAL.
 */
let inFlight: Promise<string | null> | null = null;
let initialised: Promise<void> | null = null;

export function freshAccessToken(): Promise<string | null> {
  if (!inFlight) {
    inFlight = renew().finally(() => { inFlight = null; });
  }
  return inFlight;
}

async function renew(): Promise<string | null> {
  const saved = sessionStorage.getItem('access_token');
  if (!saved) return null;
  try {
    if (!initialised) {
      initialised = msalInstance.initialize().catch((e) => { initialised = null; throw e; });
    }
    await initialised;
    const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
    if (!account) return saved;
    const { accessToken } = await msalInstance.acquireTokenSilent({ ...tokenRequest, account, forceRefresh: false });
    if (!accessToken) return saved;
    if (accessToken !== saved) sessionStorage.setItem('access_token', accessToken);
    return accessToken;
  } catch {
    return saved;
  }
}
